import debounce from "lodash/debounce";
import { useEffect, useMemo, useRef, useState } from "react";
import Input from "shared/components/Input";

const DebouncedInput = ({
  value,
  onChange,
  onBlur,
  delay = 300,
  type,
  name,
  ...rest
}) => {
  const [localValue, setLocalValue] = useState(value ?? "");
  const lastEmitted = useRef(value);

  const coerce = (raw) => {
    if (type !== "number") return raw;
    if (raw === "" || raw === null || raw === undefined) return null;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const commitRef = useRef();
  commitRef.current = (raw) => {
    const next = coerce(raw);
    lastEmitted.current = next;
    onChange?.({ target: { name, value: next } });
  };

  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setLocalValue(value ?? "");
    }
  }, [value]);

  const debouncedEmit = useMemo(
    () => debounce((raw) => commitRef.current(raw), delay),
    [delay],
  );

  useEffect(() => () => debouncedEmit.flush(), [debouncedEmit]);

  const handleChange = (e) => {
    const nextValue = e.target.value;
    setLocalValue(nextValue);
    debouncedEmit(nextValue);
  };

  const flush = (e) => {
    debouncedEmit.cancel();
    if (coerce(localValue) !== lastEmitted.current)
      commitRef.current(localValue);
    onBlur?.(e);
  };

  return (
    <Input
      {...rest}
      type={type}
      name={name}
      value={localValue}
      onChange={handleChange}
      onBlur={flush}
    />
  );
};

export default DebouncedInput;
